/**
 * <product-slide-out> — reusable, accessible product slide-out disclosure.
 *
 * Encapsulates all behaviour so it can drop into any section. A single open/closed
 * state (the `open` attribute) drives visuals (CSS) and accessibility together so
 * they can never drift (WCAG 2.2 / ARIA APG Disclosure):
 *
 * - open  -> aria-expanded="true",  panel un-inerted (in tab order + a11y tree)
 * - closed-> aria-expanded="false", panel `inert` (out of both) — belt-and-braces
 *   with the CSS visibility:hidden.
 *
 * Interaction: mouse hover (fine pointer) previews; Enter/Space/tap toggles;
 * Escape closes and restores focus; a tap/click outside closes.
 *
 * Performance: per-element listeners only (no polling); one shared document
 * listener handles outside-close for every instance.
 */
(function () {
  if (customElements.get('product-slide-out')) return;

  // Hover interactions when the device has any hovering input (a mouse/trackpad),
  // so hybrid mouse+touch devices get hover. Pure-touch devices use tap.
  var hoverQuery = window.matchMedia('(any-hover: hover)');
  var instances = new Set();

  // One shared listener closes any open slide-out — and, on touch, un-reveals a
  // tap-revealed one — when you interact outside it (and outside its banner).
  document.addEventListener('pointerdown', function (event) {
    instances.forEach(function (el) {
      var inside = el.contains(event.target);
      if (el.open && !inside) el.open = false;
      if (el.revealed && !inside && !(el.banner && el.banner.contains(event.target))) {
        el.revealed = false;
      }
    });
  });

  class ProductSlideOut extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-slideout-toggle]');
      this.panel = this.querySelector('[data-slideout-info]');
      // The element hovered to open — marked with data-slideout-card so this
      // component works for any card markup (slideshow card, hero upsell card…).
      this.card = this.querySelector('[data-slideout-card]');

      // Opt-in (data-tap-reveal): on touch, tapping the banner reveals the card.
      // The banner is the nearest hero ancestor, so its tap can toggle visibility.
      this.banner = this.hasAttribute('data-tap-reveal') ? this.closest('.index-hero') : null;

      this.addEventListener('click', this._onClick);
      this.addEventListener('keydown', this._onKeydown);
      this.addEventListener('focusout', this._onFocusout);
      if (this.card) {
        this.card.addEventListener('mouseenter', this._onEnter);
        this.card.addEventListener('mouseleave', this._onLeave);
      }
      if (this.banner) this.banner.addEventListener('click', this._onBannerTap);

      instances.add(this);
      this.open = false; // normalise: inert panel + aria-expanded=false

      // Suppress transitions until after the first paint. A collapsed rest state
      // can be a non-zero transform (e.g. the hero card sits translated off the
      // corner); without this it would animate in from 0 when the async-loaded
      // CSS first applies. CSS gates its transitions on [data-slideout-ready].
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.setAttribute('data-slideout-ready', ''));
      });
    }

    disconnectedCallback() {
      instances.delete(this);
      if (this.banner) this.banner.removeEventListener('click', this._onBannerTap);
    }

    get open() {
      return this.hasAttribute('open');
    }

    set open(value) {
      value = !!value;
      this.toggleAttribute('open', value);
      if (this.panel) this.panel.inert = !value;
      if (this.button) this.button.setAttribute('aria-expanded', value ? 'true' : 'false');
    }

    // Touch-only reveal state (mobile tap-to-reveal). Un-revealing also closes.
    get revealed() {
      return this.hasAttribute('data-revealed');
    }

    set revealed(value) {
      value = !!value;
      this.toggleAttribute('data-revealed', value);
      if (!value) this.open = false;
    }

    _onClick = (event) => {
      if (!event.target.closest('[data-slideout-toggle]')) return;
      // Enter/Space on the <button> also fire click.
      event.preventDefault();
      this.open = !this.open;
    };

    _onBannerTap = (event) => {
      // Touch only — devices with a mouse use the hover reveal instead.
      if (hoverQuery.matches) return;
      // Taps on the card itself drive open/close (its own handlers), not reveal.
      if (this.contains(event.target)) return;
      this.revealed = true;
    };

    _onEnter = () => {
      if (hoverQuery.matches) this.open = true;
    };

    _onLeave = () => {
      if (hoverQuery.matches && !this.contains(document.activeElement)) this.open = false;
    };

    _onFocusout = () => {
      requestAnimationFrame(() => {
        var hovered = this.card && this.card.matches(':hover');
        if (!this.contains(document.activeElement) && !hovered) this.open = false;
      });
    };

    _onKeydown = (event) => {
      if (event.key !== 'Escape' && event.key !== 'Esc') return;
      if (!this.open) return;
      this.open = false;
      if (this.button) this.button.focus();
    };
  }

  customElements.define('product-slide-out', ProductSlideOut);
})();
